const init = async () => {
  try {
    const warehouse = document.querySelector('.my-warehouse')
    if (!warehouse) {
      console.warn('Warehouse selector element not found');
      return;
    }

    // Check if selectedLocation is already stored in localStorage
    const storedLocation = getSelectedLocation();
    if (storedLocation) {
      const warehouseDetails = populateWarehouseDetails(storedLocation);
      warehouse.innerHTML = warehouseDetails;
      return;
    }

    const position = await getUserLocation();
    if (!position) {
      warehouse.innerHTML = '<span>Location permission needed to show nearest store</span>';
      return;
    }

    const locations = await getLocations(position);
    if (!locations.length) {
      warehouse.innerHTML = '<span>No locations found</span>';
      return;
    }

    const warehouseDetails = populateWarehouseDetails(locations[0]);
    warehouse.innerHTML = warehouseDetails;
    
    storeSelectedLocation(locations[0]);
  } catch (error) {
    console.error('Error fetching locations:', error);
  }
}

// Whether the user granted geolocation permission (set by getUserLocation)
let locationPermissionGranted = false;

// Get user location
const getUserLocation = async () => {
  const defaultLocation = { lat: 38.8892666, lon: -77.067569 }; // Center of the US East Coast
  return await new Promise(resolve => {
    if (!navigator.geolocation) {
      locationPermissionGranted = false;
      return resolve(defaultLocation);
    }
    navigator.geolocation.getCurrentPosition(
      p => {
        locationPermissionGranted = true;
        resolve({ lat: p.coords.latitude, lon: p.coords.longitude });
      },
      () => {
        locationPermissionGranted = false;
        resolve(defaultLocation);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
    );
  });
}

// Calculate distance in miles using the Haversine formula
const distMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2)**2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Get raw locations data from localStorage or API
const getLocations = async (position) => {
  // Check if locations data is already stored in localStorage
  const storedData = getStoredLocationsData();
  if (storedData) {
    return storedData;
  }

  // Fetch from API if not in localStorage
  const shopDomain = window.Shopify?.shop || '';
  const url = `https://asi-inventory-location-app.gadget.app/locations?shop=${shopDomain}`;

  let data;
  try {
    const response = await fetch(url);
    data = await response.json();
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }

  // Map through locations and add distance property, then sort by distance
  const locationsWithDistance = data.locations
    .map(location => ({
      ...location,
      distance: !locationPermissionGranted ? null : distMiles(position.lat, position.lon, location.latitude, location.longitude)
    }))
    .sort((a, b) => a.distance - b.distance);
  
  // Store the mapped and sorted data in localStorage
  storeLocationsData(locationsWithDistance);
  
  return locationsWithDistance;
}

// Strip "can make" and "quick ship" from location name
const cleanLocationName = (name) => {
  if (!name) return '';
  return name
    .replace(/\bcan make\b/gi, '')
    .replace(/\bquick ship\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Populate selected location details
const populateWarehouseDetails = (location) => {
  const cleanedName = cleanLocationName(location.name);
  const details = `
      <div class="my-warehouse-name">
        <span class="warehouse-label">My Warehouse:</span>
        <span class="selection">${cleanedName}</span>
      </div>
      <div class="my-warehouse-location">
        <span class="location">${location.address.city}, ${location.address.provinceCode}</span>
        <span class="update">Update</span>
      </div>
    `;

    return details;
}

// Get stored locations data from localStorage
const getStoredLocationsData = () => {
  try {
    const stored = localStorage.getItem('locationsData');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error retrieving locations data:', error);
    return null;
  }
}

// Store locations data in localStorage
const storeLocationsData = (locations) => {
  try {
    localStorage.setItem('locationsData', JSON.stringify(locations));
  } catch (error) {
    console.error('Error storing locations data:', error);
  }
}

// Get selected location from localStorage
const getSelectedLocation = () => {
  try {
    const stored = localStorage.getItem('selectedLocation');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error retrieving selected location:', error);
    return null;
  }
}

// Store selected location in localStorage
const storeSelectedLocation = (location) => {
  try {
    localStorage.setItem('selectedLocation', JSON.stringify(location));
    // Dispatch custom event so same-window listeners can react
    document.dispatchEvent(new CustomEvent('warehouse:location-changed', {
      detail: { location }
    }));
  } catch (error) {
    console.error('Error storing selected location:', error);
  }
}

// Handle clicks on warehouse-selector buttons to open modals
const initWarehouseSelectorClickHandler = () => {
  document.addEventListener('click', (e) => {
    const warehouseSelectorButton = e.target.closest('.warehouse-selector');
    
    if (warehouseSelectorButton) {
      const modalId = warehouseSelectorButton.getAttribute('data-action');
      if (modalId) {
        const targetModal = document.getElementById(modalId);
        if (targetModal) {
          targetModal.classList.add('open');
        }
      }
    }
  });
};

// Init - wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    init();
    initWarehouseSelectorClickHandler();
  });
} else {
  init();
  initWarehouseSelectorClickHandler();
}